/**
 * User Create Modal Component
 * Clean, responsive modal for creating new users with auto-fill functionality
 */

import React, { useState } from 'react';
import { useAutoFill } from '../../hooks/useAutoFill';
import AutoFillFieldWrapper from '../common/AutoFillFieldWrapper';
import './UserCreateModal.css';

const UserCreateModal = ({ isOpen, onClose, onSubmit, isProcessing, existingUsers = [] }) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    is_staff: false,
    is_superuser: false,
    is_active: true,
    is_approved: true,
    is_email_verified: false
  });

  const [errors, setErrors] = useState({});

  // Auto-fill functionality
  const {
    suggestions,
    isLoading: autoFillLoading,
    showSuggestions,
    currentField,
    handleFieldChange: handleAutoFillFieldChange,
    applySuggestion,
    dismissSuggestions
  } = useAutoFill(existingUsers, {
    email: { required: true, type: 'email' },
    first_name: { required: true, minLength: 2 },
    last_name: { required: true, minLength: 2 }
  }, 'user');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updatedFormData = {
        ...prev,
        [name]: fieldValue
      };

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }

      // Trigger auto-fill only for text-based fields (excluding password fields)
      if ((type === 'text' || type === 'email') && name !== 'password' && name !== 'confirm_password') {
        handleAutoFillFieldChange(name, fieldValue, updatedFormData, (field, value) => {
          setFormData(prevFormData => ({
            ...prevFormData,
            [field]: value
          }));
        });
      }

      return updatedFormData;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const userData = {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        password_confirm: formData.confirm_password,
        is_staff: formData.is_staff,
        is_superuser: formData.is_superuser,
        is_active: formData.is_active,
        is_approved: formData.is_approved,
        is_email_verified: formData.is_email_verified
      };

      await onSubmit(userData);

      // Reset form
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: '',
        is_staff: false,
        is_superuser: false,
        is_active: true,
        is_approved: true,
        is_email_verified: false
      });
      setErrors({});

    } catch (error) {
      // Error is handled by the parent component
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      confirm_password: '',
      is_staff: false,
      is_superuser: false,
      is_active: true,
      is_approved: true,
      is_email_verified: false
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Create New User</h2>
          <button className="close-btn" onClick={handleClose} disabled={isProcessing}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {errors.general && (
              <div className="error-message">{errors.general}</div>
            )}


            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="email"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="user@example.com"
                      disabled={isProcessing}
                      autoComplete="email"
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                </AutoFillFieldWrapper>
              </div>

              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="first_name"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className={errors.first_name ? 'error' : ''}
                      placeholder="Enter first name"
                      disabled={isProcessing}
                      autoComplete="given-name"
                    />
                    {errors.first_name && <span className="field-error">{errors.first_name}</span>}
                  </div>
                </AutoFillFieldWrapper>

                <AutoFillFieldWrapper
                  fieldName="last_name"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className={errors.last_name ? 'error' : ''}
                      placeholder="Enter last name"
                      disabled={isProcessing}
                      autoComplete="family-name"
                    />
                    {errors.last_name && <span className="field-error">{errors.last_name}</span>}
                  </div>
                </AutoFillFieldWrapper>
              </div>
            </div>

            {/* Security */}
            <div className="form-section">
              <h3>Security</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={errors.password ? 'error' : ''}
                    placeholder="Enter password (min 8 characters)"
                    disabled={isProcessing}
                    autoComplete="new-password"
                  />
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                <div className="form-group">
                  <label>Confirm Password *</label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    className={errors.confirm_password ? 'error' : ''}
                    placeholder="Confirm password"
                    disabled={isProcessing}
                    autoComplete="new-password"
                  />
                  {errors.confirm_password && <span className="field-error">{errors.confirm_password}</span>}
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="form-section">
              <h3>Permissions & Status</h3>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_staff"
                    checked={formData.is_staff}
                    onChange={handleInputChange}
                    disabled={isProcessing}
                  />
                  <span className="checkmark"></span>
                  Staff Member
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_superuser"
                    checked={formData.is_superuser}
                    onChange={handleInputChange}
                    disabled={isProcessing}
                  />
                  <span className="checkmark"></span>
                  Superuser
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    disabled={isProcessing}
                  />
                  <span className="checkmark"></span>
                  Active User
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_approved"
                    checked={formData.is_approved}
                    onChange={handleInputChange}
                    disabled={isProcessing}
                  />
                  <span className="checkmark"></span>
                  Approved
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_email_verified"
                    checked={formData.is_email_verified}
                    onChange={handleInputChange}
                    disabled={isProcessing}
                  />
                  <span className="checkmark"></span>
                  Email Verified
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isProcessing}
              >
                {isProcessing ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserCreateModal;
